import { useMemo } from 'react'

import { JSONType, Maybe, Scalars } from '@Generated/graphql'

function getHeight(cardAspectRatio: string, width: number): number {
  if (cardAspectRatio === 'portrait') {
    return width * 1.5
  }

  return width
}

function getWidth(cardSize: string, cardVariant: string): number {
  if (cardVariant === 'spot') {
    if (cardSize === 'tiny') {
      return 80
    }

    return 120
  }

  if (cardSize === 'medium') {
    return 370
  }

  if (cardSize === 'half') {
    return 1024
  }

  if (cardSize === 'third') {
    return 680
  }

  if (cardSize === 'fourth') {
    return 512
  }

  if (cardSize === 'tiny') {
    return 60
  }

  return 370
}

function parseStringSetting(value: JSONType, defaultValue: string): string {
  if (typeof value === 'string') {
    return value
  }

  return defaultValue
}

export function useTileSettings(settingsRaw?: Maybe<Scalars['JSON']>) {
  return useMemo(() => {
    const settings = typeof settingsRaw === 'object' && !Array.isArray(settingsRaw) ? settingsRaw || {} : {}

    const result = {
      action: parseStringSetting(settings.action, 'drawer'),
      // v2-only fields (see `version`). They live alongside the v1 fields as a superset so
      // `TileSettings` stays a single type and v1 consumers (Looks/LookList) need no changes.
      align: parseStringSetting(settings.align, 'center'),
      aspectRatio: parseStringSetting(settings.aspectRatio, 'square'),
      autoFit: typeof settings.autoFit === 'boolean' ? settings.autoFit : false,
      cardAspectRatio: parseStringSetting(settings.cardAspectRatio, 'square'),
      cardSize: parseStringSetting(settings.cardSize, 'medium'),
      cardVariant: parseStringSetting(settings.cardVariant, 'background'),
      columns: parseStringSetting(settings.columns, 'auto'),
      enableSlider: typeof settings.enableSlider === 'boolean' ? settings.enableSlider : false,
      layout: parseStringSetting(settings.layout, 'overlay'),
      padding: parseStringSetting(settings.padding, 'none'),
      rowsDesktop: typeof settings.rowsDesktop === 'number' || typeof settings.rowsDesktop === 'string' ? settings.rowsDesktop : 'auto',
      rowsMobile: typeof settings.rowsMobile === 'number' || typeof settings.rowsMobile === 'string' ? settings.rowsMobile : 'auto',
      showTitle: typeof settings.showTitle === 'boolean' ? settings.showTitle : true,
      size: parseStringSetting(settings.size, 'large'),
      titleLines: parseStringSetting(settings.titleLines, '1'),
      type: parseStringSetting(settings.type, 'latest'),
      version: parseStringSetting(settings.version, '1'),
    }

    const width = getWidth(result.cardSize, result.cardVariant)

    return {
      ...result,
      height: getHeight(result.cardAspectRatio, width),
      width,
    }
  }, [settingsRaw])
}

export type TileSettings = ReturnType<typeof useTileSettings>
